import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Card,
    Typography,
    TextField,
    Fab,
    Grid,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    Divider,
    Chip,
    Alert,
    Tooltip,
} from '@mui/material';
import { Login, Logout, ErrorOutline } from '@mui/icons-material';
import theme from '../../../assets/theme';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useNotification } from '../../../context/notification.context';
import { useLoading } from "../../../context/loading.context";
import { checkInReservation, checkOutReservation } from '../../../service/network';
import CustomButton from '../../../components/Buttons';

export default function Check_IN_Out({ reservation, confirmationStatus, tariff, details, unallocatedAmount }) {
    const [hour, setHour] = useState('00');
    const [minute, setMinute] = useState('00');
    const [date, setDate] = useState('');
    const [isCheckedIn, setIsCheckedIn] = useState(false);
    const [isCheckedOut, setIsCheckedOut] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const { showNotification } = useNotification();
    const { showLoader, hideLoader } = useLoading();
    console.log("tariff", tariff)

    // Get the latest confirmation status (single object or from array)
    const latestConfirmation = useMemo(() => {
        if (!confirmationStatus) return null;

        if (Array.isArray(confirmationStatus)) {
            return confirmationStatus.length > 0
                ? confirmationStatus[confirmationStatus.length - 1]
                : null;
        }
        return confirmationStatus;
    }, [confirmationStatus]);

    // Initialize date/time/room and status
    useEffect(() => {
        const today = new Date();
        const formattedDate = today.toISOString().split('T')[0];

        // Initialize with current date/time by default
        setDate(formattedDate);
        setHour(String(today.getHours()).padStart(2, '0'));
        setMinute(String(today.getMinutes()).padStart(2, '0'));

        // Check reservation status and confirmation status
        const isCheckedInStatus = reservation?.status === 'Checked-In';
        const isCheckedOutStatus = reservation?.status === 'Checked-Out';
        const hasCheckInDate = latestConfirmation?.check_in_date;
        const hasCheckOutDate = latestConfirmation?.check_out_date;

        // Update status based on confirmation data
        setIsCheckedIn(hasCheckInDate || isCheckedInStatus);
        setIsCheckedOut(hasCheckOutDate || isCheckedOutStatus);

        // If we have confirmation data, use those dates/times
        if (latestConfirmation) {
            if (hasCheckInDate && !hasCheckOutDate) {
                // Use check-in time
                const [datePart, timePart] = latestConfirmation.check_in_date.split(' ');
                const [hours, minutes] = timePart.split(':');
                setDate(datePart);
                setHour(hours);
                setMinute(minutes);
            } else if (hasCheckOutDate) {
                // Use check-out time
                const [datePart, timePart] = latestConfirmation.check_out_date.split(' ');
                const [hours, minutes] = timePart.split(':');
                setDate(datePart);
                setHour(hours);
                setMinute(minutes);
            }
        }
    }, [reservation, latestConfirmation]);

    const handleCheckIn = async () => {
        setIsLoading(true);
        showLoader(true);

        try {
            const checkInDateTime = `${date} ${hour}:${minute}:00`;

            const payload = {
                room: reservation.room,
                name: reservation.name,
                check_in: checkInDateTime
            };

            const response = await checkInReservation(payload);

            if (response?.status === 200 || response?.data?.message || response?.data?.data) {
                showNotification('success', 'Success', 'Check-in successful');
                setIsCheckedIn(true);
                window.location.reload();
            } else {
                throw new Error(response?.data?.message || 'Failed to check in');
            }
        } catch (error) {
            console.error('Check-in error:', error);
            let errorMessage = 'Failed to check in';

            if (error.response?.data?._server_messages) {
                try {
                    const messages = JSON.parse(error.response.data._server_messages);
                    errorMessage = messages.map(msg => {
                        try {
                            const parsed = JSON.parse(msg);
                            return parsed.message.replace(/<[^>]+>/g, '');
                        } catch {
                            return msg;
                        }
                    }).join(', ');
                } catch (parseError) {
                    console.error('Error parsing server messages:', parseError);
                }
            } else if (error.message) {
                errorMessage = error.message;
            }

            showNotification('error', 'Error', errorMessage);
        } finally {
            setIsLoading(false);
            hideLoader();
        }
    };

    const handleCheckOut = async () => {
        setIsLoading(true);
        showLoader(true);

        try {
            const checkOutDateTime = `${date} ${hour}:${minute}:00`;

            const payload = {
                room: reservation.room,
                name: reservation.name,
                check_out: checkOutDateTime
            };

            const response = await checkOutReservation(payload);

            if (response?.status === 200 || response?.data?.message || response?.data?.data) {
                showNotification('success', 'Success', 'Check-out successful');
                setIsCheckedOut(true);
                window.location.reload();
            } else {
                throw new Error(response?.data?.message || 'Failed to check out');
            }
        } catch (error) {
            console.error('Check-out error:', error);
            let errorMessage = 'Failed to check out';

            if (error.response?.data?._server_messages) {
                try {
                    const messages = JSON.parse(error.response.data._server_messages);
                    errorMessage = messages.map(msg => {
                        try {
                            const parsed = JSON.parse(msg);
                            return parsed.message.replace(/<[^>]+>/g, '');
                        } catch {
                            return msg;
                        }
                    }).join(', ');
                } catch (parseError) {
                    console.error('Error parsing server messages:', parseError);
                }
            } else if (error.message) {
                errorMessage = error.message;
            }

            showNotification('error', 'Error', errorMessage);
        } finally {
            setIsLoading(false);
            hideLoader();
        }
    };

    const handleConfirmation = () => {
        setOpenDialog(false);
        if (pendingAction === 'checkIn') {
            handleCheckIn();
        } else if (pendingAction === 'checkOut') {
            handleCheckOut();
        }
    };

    const getStatusText = () => {
        if (isCheckedOut) return 'Check In';
        if (isCheckedIn) return 'Check Out';
        return 'Check-in';
    };

    const getStatusColor = () => {
        if (isCheckedOut) return theme.palette.primary.main;
        if (isCheckedIn) return theme.palette.error.main;
        return theme.palette.primary.main;
    };

    const getFabColor = () => {
        if (isCheckedOut) return 'default';
        if (isCheckedIn) return 'error';
        return 'primary';
    };

    const getFabIcon = () => {
        if (isCheckedIn && !isCheckedOut) return <Logout />;
        return <Login />;
    };

    const handleFabClick = () => {
        if (isCheckedIn && !isCheckedOut) {
            setPendingAction('checkOut');
            setOpenDialog(true);
        } else {
            setPendingAction('checkIn');
            setOpenDialog(true);
        }
    };

    const getDialogTitle = () => {
        if (pendingAction === 'checkIn') return 'Confirm Check In';
        if (pendingAction === 'checkOut') return 'Confirm Check Out';
        return 'Confirm Action';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return dayjs(dateString).format('DD-MM-YYYY');
    };
    const hasCheckoutIssues = useMemo(() => {
        if (pendingAction !== 'checkOut') return false;

        // Check if checkout date is same as booked to date
        const checkoutDate = dayjs(date).format('YYYY-MM-DD');
        const bookedToDate = dayjs(reservation?.to_date).format('YYYY-MM-DD');
        const isSameDate = checkoutDate === bookedToDate;

        // Check if payment issues exist
        const amountDue = (details?.invoiced_amount ?? 0) - (details?.paid_amount ?? 0);
        const hasPaymentIssues = amountDue > 0 || unallocatedAmount > 0;

        return { isSameDate, hasPaymentIssues };
    }, [pendingAction, date, reservation?.to_date, details, unallocatedAmount]);

    const getDialogContent = () => {
        const actionTime = `${date} ${hour}:${minute}`;
        const checkInTime = latestConfirmation?.check_in_date || 'Not checked in yet';
        const checkOutTime = latestConfirmation?.check_out_date || 'Not checked out yet';
        const invoiceAmount = latestConfirmation?.invoice_amount || '0.00';
        const amountDue = (reservation?.invoiced_amount ?? 0) - (reservation?.paid_amount ?? 0);


        console.log("det", details)
        return (
            <Box sx={{
                mt: 2,
                '& .MuiGrid-item': {
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}>

                {/* Header with icon */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 3,
                    p: 2,
                    backgroundColor: pendingAction === 'checkIn'
                        ? 'rgba(25, 118, 210, 0.08)'
                        : 'rgba(211, 47, 47, 0.08)',
                    borderRadius: 2,
                    borderLeft: `4px solid ${pendingAction === 'checkIn'
                        ? theme.palette.primary.main
                        : theme.palette.error.main}`
                }}>
                    {pendingAction === 'checkIn' ? (
                        <Login sx={{
                            color: theme.palette.primary.main,
                            fontSize: 32,
                            mr: 2
                        }} />
                    ) : (
                        <Logout sx={{
                            color: theme.palette.error.main,
                            fontSize: 32,
                            mr: 2
                        }} />
                    )}
                    <Box>
                        <Typography variant="h6" fontWeight="600">
                            {pendingAction === 'checkIn' ? 'Confirm Check-In' : 'Confirm Check-Out'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {pendingAction === 'checkIn'
                                ? 'Please verify the check-in details'
                                : 'Please review the payment information'}
                        </Typography>
                    </Box>
                </Box>

                {/* Reservation Info Card */}
                <Box sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 2,
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }
                }}>
                    <Typography variant="subtitle1" fontWeight="500" gutterBottom>
                        Reservation Details
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={3.3}>
                            <Typography variant="caption" color="text.secondary">
                                RESERVATION ID
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                                {reservation?.name || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid size={3.3}>
                            <Typography variant="caption" color="text.secondary">
                                ROOM NUMBER
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                                {details?.room_name
                                    || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid size={5.4}>
                            <Typography variant="caption" color="text.secondary">
                                BOOKED DATES
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                    label={formatDate(reservation?.from_date)}
                                    variant="outlined"
                                    color="primary"
                                />
                                <Typography variant="body1">to</Typography>
                                <Chip
                                    label={formatDate(reservation?.to_date)}
                                    variant="outlined"
                                    color="primary"

                                />
                                {pendingAction === 'checkOut' && !hasCheckoutIssues.isSameDate && (
                                    <Tooltip title="Check-out date matches booked end date">
                                        <ErrorOutline color="error" fontSize="small" />
                                    </Tooltip>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Time Information */}
                <Box sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 2,
                    backgroundColor: pendingAction === 'checkIn'
                        ? 'rgba(25, 118, 210, 0.05)'
                        : 'rgba(211, 47, 47, 0.05)',
                    border: `1px solid ${pendingAction === 'checkIn'
                        ? 'rgba(25, 118, 210, 0.3)'
                        : 'rgba(211, 47, 47, 0.3)'}`,
                    transition: 'all 0.3s ease'
                }}>
                    <Typography variant="subtitle1" fontWeight="500" gutterBottom>
                        {pendingAction === 'checkIn' ? 'Check-In Time' : 'Check-Out Time'}
                    </Typography>

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        backgroundColor: 'white',
                        borderRadius: 1,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {pendingAction === 'checkIn' ? 'Check-in at:' : 'Check-out at:'}
                        </Typography>
                        <Typography variant="h6"
                            color={pendingAction === 'checkIn' ? 'primary.main' : 'error.main'}
                            fontWeight="600">
                            {actionTime}
                        </Typography>
                    </Box>


                </Box>

                {/* Payment Information (only for check-out) */}
                {pendingAction === 'checkOut' && (
                    <Box sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(101, 101, 101, 0.05)',
                        border: '1px solid rgba(0, 0, 0, 0.1)'
                    }}>
                        <Typography variant="subtitle1" fontWeight="500" gutterBottom>
                            Payment Summary
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid size={4}>
                                <Typography variant="caption" color="text.secondary">
                                    INVOICE AMOUNT
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {`₹ ${details?.invoiced_amount ?? 0}`}
                                </Typography>
                            </Grid>
                            <Grid size={4}>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Typography variant="caption" color="text.secondary">
                                        AMOUNT DUE
                                    </Typography>
                                    {((reservation?.invoiced_amount ?? 0) - (reservation?.paid_amount ?? 0)) > 0 && (
                                        <Tooltip title="Outstanding payment due">
                                            <ErrorOutline color="error" fontSize="small" />
                                        </Tooltip>
                                    )}
                                </Box>
                                <Typography variant="body1"
                                    color={((reservation?.invoiced_amount ?? 0) - (reservation?.paid_amount ?? 0)) > 0 ? 'error.main' : 'success.main'}
                                    fontWeight="600">
                                    {`₹ ${(reservation?.invoiced_amount ?? 0) - (reservation?.paid_amount ?? 0)}`}
                                </Typography>
                            </Grid>
                            <Grid size={4}>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Typography variant="caption" color="text.secondary">
                                        UNALLOCATED AMOUNT
                                    </Typography>
                                    {unallocatedAmount !== 0 && (
                                        <Tooltip title="Unallocated amount exists">
                                            <ErrorOutline color="error" fontSize="small" />
                                        </Tooltip>
                                    )}
                                </Box>
                                <Typography variant="body1" fontWeight="500"
                                    color={(unallocatedAmount != 0) ? 'error.main' : 'text.primary'}>
                                    ₹{unallocatedAmount}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* Footer Note */}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
                    {pendingAction === 'checkIn'
                        ? 'This action will mark the guest as checked-in'
                        : 'This action will finalize the check-out process'}
                </Typography>
            </Box>
        );
    };

    const isFabDisabled = () => {
        if (isLoading) return true;
        if (!reservation?.room) return true;
        return false;
    };
    console.log("res", details)

    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
        >
            <Card
                sx={{
                    p: 1.5,
                    borderRadius: '20px',
                    backgroundColor: theme.palette.light.main,
                    width: '100%',
                    boxShadow: 3,
                    transition: 'border 0.3s ease'
                }}
            >
                <Typography
                    variant="SubTitleBold"
                    sx={{
                        color: getStatusColor()
                    }}
                >
                    {getStatusText()}
                </Typography>
                <Grid container spacing={1}>
                    {/* Date and Time Row */}
                    <Grid container size={12} spacing={1}>
                        {/* Date - Left Side */}
                        <Grid size={5}>
                            <Typography variant="body2" color="text.primary" mb={0.5}>
                                Date
                            </Typography>
                            <TextField
                                type="date"
                                fullWidth
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                disabled={isCheckedOut}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: theme.borderRadius.medium,
                                    }
                                }}
                            />
                        </Grid>

                        {/* Spacer */}
                        <Grid size={2} />

                        {/* Time - Right Side */}
                        <Grid size={5}>
                            <Typography variant="body2" color="text.primary" mb={0.5}>
                                Time
                            </Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <MobileTimePicker
                                    value={dayjs().hour(hour).minute(minute)}
                                    onChange={(newValue) => {
                                        if (newValue) {
                                            setHour(String(newValue.hour()).padStart(2, '0'));
                                            setMinute(String(newValue.minute()).padStart(2, '0'));
                                        }
                                    }}
                                    ampm={false}
                                    minutesStep={1}
                                    disabled={isCheckedOut}
                                    slotProps={{
                                        textField: {
                                            variant: 'outlined',
                                            sx: {
                                                width: '100%',
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: `${theme.borderRadius.medium} !important`,
                                                },
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: theme.palette.grey[400],
                                                },
                                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: theme.palette.primary.main,
                                                },
                                            },
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>

                    {/* Room No. and Buttons Row */}
                    <Grid size={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            {/* Room No. - Left Aligned */}
                            <Box width="40%">
                                <Typography variant="body2" color="text.primary" mb={0.5}>
                                    Room No.
                                </Typography>
                                <TextField
                                    fullWidth
                                    value={details?.room_name || ''}
                                    placeholder='Room No'
                                    disabled
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: theme.borderRadius.medium,
                                            backgroundColor: theme.palette.lightgray.main,
                                        }
                                    }}
                                />
                            </Box>

                            {/* Buttons - Right Aligned */}
                            <Box display="flex" alignItems="center" gap={1} mt={2}>
                                <Fab
                                    color={getFabColor()}
                                    sx={{
                                        backgroundColor: getStatusColor(),
                                        color: '#fff',
                                        boxShadow: `0px 4px 12px ${getStatusColor()}40`,
                                        '&:hover': {
                                            backgroundColor: getStatusColor(),
                                            opacity: 0.9
                                        },
                                    }}
                                    onClick={handleFabClick}
                                    disabled={isFabDisabled()}
                                >
                                    {isLoading ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        getFabIcon()
                                    )}
                                </Fab>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Card>

            {/* Confirmation Dialog */}
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                    },
                }}
            >
                <DialogTitle id="alert-dialog-title">
                    {getDialogTitle()}
                </DialogTitle>
                <DialogContent>
                    {getDialogContent()}
                </DialogContent>
                <DialogActions sx={{ mb: 2 }}>
                    <CustomButton
                        variant="outlined"
                        sx={{
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            borderRadius: theme.borderRadius.medium,
                            border: "2px solid var(--Primary-Blue-MB, #27394D)",
                            textTransform: 'none',
                            '&:hover': { borderColor: 'primary.dark' },
                            px: 1.5,
                            py: 0.75,
                            minWidth: '120px',
                            flexShrink: 0,
                        }}
                    >
                        Cancel
                    </CustomButton>
                    <CustomButton
                        variant="outlined"
                        sx={{

                            backgroundColor: "primary.main",
                            color: 'light.main',
                            borderRadius: theme.borderRadius.medium,
                            textTransform: 'none',
                            '&:hover': { borderColor: 'primary.dark' },
                            px: 1.5,
                            py: 0.75,
                            minWidth: '150px',
                            flexShrink: 0,
                        }}
                        onClick={handleConfirmation}
                    >
                        Confirm
                    </CustomButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
}